import React, { useState, useEffect } from 'react'
import { observer } from "mobx-react-lite";
import { Draggable } from 'react-beautiful-dnd';
import {
  ChevronDown, ChevronUp, ChatDots,
  Copy, Envelope, BoxArrowUpRight, PlusLg
} from 'react-bootstrap-icons'
import axiosService from '../../../../../utils/AxiosService';
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import ReachbirdApprovedBadge from '../../../../Shared/ReachbirdApprovedBadge'
import { formatNumber } from '../../../../../utils/Helper'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import { useNavigate, useParams } from 'react-router-dom'
import PlatformButton from './PlatformButton'
import SelectionStatusIcon from '../../../../Shared/SelectionStatusIcon'
import { isUgcCreatorTag } from '../../../../../utils/tagStyles'

export default observer(function InfluencerItem({ auth, index }){
  const { id } = useParams()

  const [influencer, setInfluencer] = useState({});
  const [authentications, setAuthentications] = useState([])

  const getInfluencer = () => {
    axiosService.get(`/api/advertiser/campaign_share/authentications/${auth.authentication_id}/influencer`)
      .then(influencer => setInfluencer(influencer))
      .catch(({ data: { error } }) => toastError(error))
  }

  const getAuthentications = () => {
    axiosService.get(`/api/advertiser/campaign_share/authentications/${auth.authentication_id}/authentications`)
      .then(authentications => {
        setAuthentications(authentications)
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  useEffect(() => {
    getInfluencer()
    getAuthentications()
  }, [])

  const authCampTags = auth.tags.reduce((result, tag) => {
    if (tag.campaign_id === +id) {
      result.push(tag)
    }
    return result
  }, [])

  const authTags = auth.tags.reduce((result, tag) => {
    if (tag.campaign_id === null) {
      result.push(tag)
    }
    return result
  }, [])

  const sampleTags = authCampTags.slice(0,2).concat(authTags.slice(0, 2))

  const tagBgColor = (campId, tag) => isUgcCreatorTag(tag) ? '#F5F3FF' : !campId ? '#EFF6FF' : '#FEFCE8'
  const tagTxtColor = (campId, tag) => isUgcCreatorTag(tag) ? '#7C3AED' : !campId ? '#2563EB' : '#EAB308'

  const navigate = useNavigate()
  const [rolledUp, setRolledUp] = useState(true)
  const [selectedAuth, setSelectedAuth] = useState(auth.provider)

  const copyToClipboard = () => {
    navigator.clipboard.writeText(influencer.email)
    .then(() => toastSuccess(`Copied to clipboard: ${influencer.email}`))
    .catch(() => toastError('failed to copy email'))
  }

  return (
    <>
      <Draggable draggableId={`${auth.authentication_id}`} index={index} type="influencer" isDragDisabled>
        {(provided, snapshot) => (
          <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            className={`border-1 shadow rounded-lg mb-3 ${auth.isSelected ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green' : 'bg-white dark:bg-rb-dark-mode border-grey-100 dark:border-rb-dark-mode-lighter'} max-h-max ${snapshot.isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          >
            <div
              className='flex flex-col flex-grow'
              onClick={() => {
                auth.isSelected = true
                const selectedAuthentication = authentications.find(a => a.provider === selectedAuth)
                let url = ''
                if (selectedAuthentication) {
                  url = `/advertiser/campaign_share/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/authentication/${selectedAuthentication.id}/overview`
                } else if (authentications.length > 0) {
                  url = `/advertiser/campaign_share/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/authentication/${authentications[0].id}/overview`
                } else {
                  url = `/advertiser/campaign_share/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/profile`
                }
                if (url.includes('/authentication/') && url.includes('/overview')) {
                  window.open(url, '_blank')
                } else {
                  navigate(url)
                }
              }}
            >
              <div className="p-3 flex min-w-0 justify-between items-center gap-2 flex-grow">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <img src={auth.avatar} className='shrink-0 bg-rb-green border-1 border-grey-500 rounded-full w-8 h-8'/>
                  <span className="min-w-0 flex-1 truncate dark:text-white" title={auth.username}>{auth.username}</span>
                  {auth.reachbird_approved && <span className="shrink-0"><ReachbirdApprovedBadge compact /></span>}
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); setRolledUp(!rolledUp) }} className="shrink-0 flex items-center rounded-lg p-2 dark:text-white">
                  {rolledUp ? <ChevronDown /> : <ChevronUp />}
                </button>
              </div>

              <div className={`border-b-1 ${auth.isSelected ? 'border-rb-green dark:border-rb-green' : 'border-grey-100 dark:border-rb-dark-mode-lighter'}`} />
              {
                !rolledUp && (
                  <>
                    <div className="flex items-center p-1">
                      {authentications.map(authentication => (
                        <PlatformButton
                          key={authentication.id}
                          platform={authentication.provider}
                          isSelected={selectedAuth === authentication.provider}
                          onClick={() => setSelectedAuth(authentication.provider)}
                        />
                      ))}
                    </div>
                    <div className={`border-b-1 ${auth.isSelected ? 'border-rb-green dark:border-rb-green': 'border-grey-100 dark:border-rb-dark-mode-lighter'}`} />
                  </>
                )
              }

              <div className="flex justify-between p-3 pb-1 items-center">
                <div className='flex items-center'>
                  <p className='mr-1 dark:text-white'>Reach</p>
                  <span className='text-rb-green font-bold'>{formatNumber(authentications?.find(auth => auth.provider === selectedAuth)?.reach || "Unknown", 0)}</span>
                  <div className="flex ml-3">
                    {(auth.country || auth.main_country) && getUnicodeFlagIcon(auth.country || auth.main_country)}
                    <p className="ml-1 dark:text-white">{auth.country || auth.main_country}</p>
                  </div>
                  <div className="ml-2">
                    <SelectionStatusIcon selectionStatus={auth.selection_status ?? 'pending'} />
                  </div>
                </div>
                <div className='flex items-center text-xs gap-1 cursor-pointer' onClick={(e) => {
                  e.stopPropagation()
                  auth.isSelected = true
                  navigate(`/advertiser/campaign_share/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/client_thread`)
                }}>
                  <p className="dark:text-white">{auth.comments.length + auth.client_comments.length}</p>
                  <ChatDots className="dark:text-white"/>
                </div>
              </div>
            </div>
            {
              !rolledUp && (
                <div>
                  <div className="px-3">
                    <button
                      className='my-2 w-full px-4 flex justify-start items-center bg-white dark:bg-rb-dark-mode-lighter p-1 border-2 border-gray-100 dark:border-rb-dark-mode-lighter rounded-lg dark:text-white'
                      onClick={() => {
                        auth.isSelected = true
                        navigate(`/advertiser/campaign_share/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/client_thread`)
                      }}
                    >
                      <Envelope className='text-rb-black dark:text-white mr-2'/>
                      <span>Open Threads</span>
                    </button>
                  </div>
                  <div className="px-3">
                    <div className="my-4 flex gap-2 flex-wrap">
                      {
                        sampleTags.map((tag) => (
                          <span key={tag.id} title={isUgcCreatorTag(tag) ? 'Manually added UGC creator' : undefined} className='px-3 py-1 rounded-full flex items-center gap-1 w-fit' style={{ backgroundColor: tagBgColor(tag.campaign_id, tag) }}>
                            <span style={{ color: tagTxtColor(tag.campaign_id, tag) }}>{isUgcCreatorTag(tag) ? `★ ${tag.name}` : tag.name}</span>
                          </span>
                        ))
                      }
                      {
                        sampleTags.length > 0 && (
                          <span className='px-3 py-1 rounded-full flex items-center gap-1 w-fit bg-gray-100 dark:bg-rb-dark-mode-lighter dark:text-white'>
                            <span>+{(authCampTags.length + authTags.length) - sampleTags.length}</span>
                          </span>
                        )
                      }
                      <button
                        className='hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter rounded-lg p-2 dark:text-white'
                        type='button'
                        onClick={() => {
                          auth.isSelected = true
                          navigate(`/advertiser/campaign_share/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/profile`)
                        }}
                      >
                        <PlusLg className='text-lg' />
                      </button>
                    </div>
                  </div>
                </div>
              )
            }
          </div>
        )}
      </Draggable>
    </>
  )
})
